import { format } from 'date-fns'
import { RotateCcwKey, Settings, TriangleAlert } from 'lucide-react'
import { useParams } from 'react-router'
import { Badge } from '@/components/sv/badge'
import { CopyField } from '@/components/sv/copy-field'
import { InfoBox } from '@/components/sv/info-box'
import { H3 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  BasicDetailsQuery,
  BasicDetailsQueryVariables,
} from '@/routes/webhook/tabs/overview/sections/general/details/index.types.gen'

const WEBHOOK_BASIC_DETAILS_QUERY = gql`
  query BasicDetails($idCompany: String!, $idWebhook: String!) {
    company(id: $idCompany) {
      webhook(id: $idWebhook) @live {
        id
        isEnabled
        secret
        oldSecret
        oldSecretExpiredAt
        endpoint
        allowedEvents
        description
        createdAt
        updatedAt
      }
    }
  }
`

export const WebhookDetailsSection = () => {
  const { idWebhook = '' } = useParams()
  const { navigateToPath } = useCompanyPath()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    BasicDetailsQuery,
    BasicDetailsQueryVariables
  >(WEBHOOK_BASIC_DETAILS_QUERY, {
    variables: { idCompany, idWebhook },
  })

  const webhook = data?.company?.webhook

  return (
    <>
      <CardHeader className="py-6">
        <CardTitle>
          <H3>Details</H3>
        </CardTitle>
        <CardAction className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigateToPath({
                pathname: '/webhook/:idWebhook/overview/roll-secret',
                variables: { idWebhook },
                options: { replace: true },
              })
            }
          >
            <RotateCcwKey aria-hidden="true" />
            Roll secret
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              navigateToPath({
                pathname: '/webhook/:idWebhook/overview/update',
                variables: { idWebhook },
                options: { replace: true },
              })
            }
          >
            <Settings aria-hidden="true" />
            Update
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-y-4 gap-x-6 text-sm grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <InfoBox
            label="Status"
            value={
              webhook?.isEnabled ? (
                <Badge variant="emerald">Active</Badge>
              ) : (
                <Badge variant="destructive">Disabled</Badge>
              )
            }
          />
          <InfoBox
            label="Endpoint"
            value={<CopyField value={webhook?.endpoint} size="sm" />}
          />
          <InfoBox label="Description" value={webhook?.description || '-'} />
          <InfoBox
            label="Allowed events"
            value={
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    {webhook?.allowedEvents?.length ?? 0}{' '}
                    {(webhook?.allowedEvents?.length ?? 0) > 1
                      ? 'events'
                      : 'event'}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[500px]">
                  {webhook?.allowedEvents?.join(', ')}
                </TooltipContent>
              </Tooltip>
            }
          />
          <InfoBox
            label={
              <div className="flex items-center gap-2">
                Signing secret{' '}
                {webhook?.oldSecret ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <TriangleAlert className="size-4 text-warning" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      This secret will be activated at{' '}
                      <strong>
                        {format(
                          webhook?.oldSecretExpiredAt,
                          'MMM dd, HH:mm:ss'
                        )}
                      </strong>
                      <br />
                      The old secret is still used:{' '}
                      <span className="font-mono">{webhook?.oldSecret}</span>
                      <br />
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
            }
            value={<CopyField value={webhook?.secret} isSensitive size="sm" />}
          />
          <InfoBox
            label="ID"
            value={<CopyField value={webhook?.id} size="sm" />}
          />
          <InfoBox
            label="Created"
            value={
              webhook?.createdAt
                ? format(webhook?.createdAt ?? '', 'MMM d, yyyy, h:mm a')
                : ''
            }
          />
          <InfoBox
            label="Updated"
            value={
              webhook?.updatedAt
                ? format(webhook?.updatedAt ?? '', 'MMM d, yyyy, h:mm a')
                : ''
            }
          />
        </div>
      </CardContent>
    </>
  )
}
